import { useMemo, useRef, useState } from 'react';
import { Box, Chip, Sheet, Stack, Typography } from '@mui/joy';


/**
 * Линейный график динамики на чистом SVG.
 *
 * Библиотеку не тянем: ради одного графика она добавила бы сотню
 * с лишним килобайт в бандл и новую зависимость в сборке. Если recharts
 * в проекте уже есть, этот компонент меняется на него точечно —
 * остальной код о нём ничего не знает.
 *
 * Одна метрика за раз намеренно: показы измеряются десятками тысяч,
 * CTR процентами, расход рублями. На одной оси это превращается
 * в прямую линию и одну зубчатую.
 */

const PADDING = { top: 16, right: 16, bottom: 28, left: 52 };


export default function AdLineChart({
    data = [], metrics = [], height = 260,
}) {
    const [activeMetric, setActiveMetric] = useState(metrics[0]?.key);
    const [hoverIndex, setHoverIndex] = useState(null);
    const svgRef = useRef(null);

    const metric = metrics.find((m) => m.key === activeMetric) || metrics[0];

    const { points, maxValue, width } = useMemo(() => {
        const w = 800; // рисуем в фиксированной системе координат, растягивает viewBox
        const values = data.map((row) => Number(row?.[metric?.key] ?? 0));
        const max = Math.max(...values, 0) || 1;
        const innerW = w - PADDING.left - PADDING.right;
        const innerH = height - PADDING.top - PADDING.bottom;

        const pts = values.map((value, index) => ({
            x: PADDING.left + (data.length <= 1
                ? innerW / 2
                : (index / (data.length - 1)) * innerW),
            y: PADDING.top + innerH - (value / max) * innerH,
            value,
            row: data[index],
        }));

        return { points: pts, maxValue: max, width: w };
    }, [data, metric?.key, height]);

    if (!metric || !data.length) {
        return (
            <Sheet variant="outlined" sx={{ p: 4, borderRadius: 'md', textAlign: 'center' }}>
                <Typography level="body-sm" color="neutral">
                    За выбранный период данных нет
                </Typography>
            </Sheet>
        );
    }

    const linePath = points
        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
        .join(' ');

    const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(1)} `
        + `${height - PADDING.bottom} L ${points[0].x.toFixed(1)} ${height - PADDING.bottom} Z`;

    // Подписи по оси X: не чаще, чем помещается без наложения
    const labelStep = Math.max(1, Math.ceil(data.length / 8));

    const handleMove = (event) => {
        const svg = svgRef.current;
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        const relativeX = ((event.clientX - rect.left) / rect.width) * width;
        let nearest = 0;
        let best = Infinity;
        points.forEach((p, index) => {
            const distance = Math.abs(p.x - relativeX);
            if (distance < best) { best = distance; nearest = index; }
        });
        setHoverIndex(nearest);
    };

    const hovered = hoverIndex !== null ? points[hoverIndex] : null;

    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                {metrics.map((m) => (
                    <Chip
                        key={m.key}
                        variant={m.key === activeMetric ? 'solid' : 'outlined'}
                        color={m.key === activeMetric ? 'primary' : 'neutral'}
                        onClick={() => setActiveMetric(m.key)}
                        sx={{ cursor: 'pointer' }}
                    >
                        {m.label}
                    </Chip>
                ))}
            </Stack>

            <Box sx={{ position: 'relative' }}>
                <svg
                    ref={svgRef}
                    viewBox={`0 0 ${width} ${height}`}
                    preserveAspectRatio="none"
                    style={{ width: '100%', height, display: 'block' }}
                    onMouseMove={handleMove}
                    onMouseLeave={() => setHoverIndex(null)}
                >
                    {/* Горизонтальная сетка и подписи оси Y */}
                    {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
                        const y = PADDING.top + (height - PADDING.top - PADDING.bottom) * (1 - fraction);
                        return (
                            <g key={fraction}>
                                <line
                                    x1={PADDING.left} x2={width - PADDING.right}
                                    y1={y} y2={y}
                                    stroke="currentColor" strokeOpacity={0.12} strokeWidth={1}
                                />
                                <text
                                    x={PADDING.left - 8} y={y + 4}
                                    textAnchor="end" fontSize={11}
                                    fill="currentColor" fillOpacity={0.5}
                                >
                                    {metric.format
                                        ? metric.format(maxValue * fraction, true)
                                        : Math.round(maxValue * fraction)}
                                </text>
                            </g>
                        );
                    })}

                    <defs>
                        <linearGradient id="adChartFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="var(--joy-palette-primary-400)" stopOpacity="0.28" />
                            <stop offset="100%" stopColor="var(--joy-palette-primary-400)" stopOpacity="0" />
                        </linearGradient>
                    </defs>

                    <path d={areaPath} fill="url(#adChartFill)" />
                    <path
                        d={linePath}
                        fill="none"
                        stroke="var(--joy-palette-primary-500)"
                        strokeWidth={2}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                    />

                    {/* Точки рисуем, только когда их немного — иначе линия превращается в гусеницу */}
                    {points.length <= 40 && points.map((p, index) => (
                        <circle
                            key={index}
                            cx={p.x} cy={p.y} r={hoverIndex === index ? 5 : 3}
                            fill="var(--joy-palette-primary-500)"
                            stroke="var(--joy-palette-background-surface)"
                            strokeWidth={2}
                        />
                    ))}

                    {hovered && (
                        <line
                            x1={hovered.x} x2={hovered.x}
                            y1={PADDING.top} y2={height - PADDING.bottom}
                            stroke="currentColor" strokeOpacity={0.25} strokeDasharray="3 3"
                        />
                    )}

                    {/* Подписи оси X */}
                    {points.map((p, index) => (
                        index % labelStep === 0 ? (
                            <text
                                key={`x-${index}`}
                                x={p.x} y={height - 8}
                                textAnchor="middle" fontSize={10}
                                fill="currentColor" fillOpacity={0.5}
                            >
                                {String(p.row?.date || '').slice(5).replace('-', '.')}
                            </text>
                        ) : null
                    ))}
                </svg>

                {hovered && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: `${(hovered.x / width) * 100}%`,
                            top: 0,
                            transform: 'translateX(-50%)',
                            bgcolor: 'background.surface',
                            border: '1px solid', borderColor: 'divider',
                            borderRadius: 'sm', px: 1, py: 0.5,
                            pointerEvents: 'none', whiteSpace: 'nowrap',
                            boxShadow: 'sm',
                        }}
                    >
                        <Typography level="body-xs" color="neutral">
                            {hovered.row?.date}
                        </Typography>
                        <Typography level="body-sm" sx={{ fontWeight: 700 }}>
                            {metric.format ? metric.format(hovered.value) : hovered.value}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Sheet>
    );
}